import React, { useEffect, useRef } from "react";
import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material";
import WidgetFrame, { WidgetButton } from "./WidgetFrame";
import FieldControl from "./fields/FieldControl";
import { otherKey, useWidgetForm } from "./useWidgetForm";
import { useWidgetMsg } from "../../../hooks/widget/useWidgetMsg";
import { reportWidgetEvent } from "../../../utils/widgetTelemetry";

/**
 * `data_collection` (spec-widget-mcp 05 Step 5; D-W14, §7 "Rule: the stepper"). The composer decides
 * `payload.layout` — a form, or a stepper from the workspace's stepper threshold up (pass-2 P2-H-WM09-1,
 * 2026-10-01: the Studio Customize tab's threshold now reaches the customer) — and this component renders
 * it; Back/Next belong here (never the payload). An envelope without `layout` (an older server) keeps
 * the built-in rule: two or more groups are a stepper.
 *   - Next is disabled while a visible required field of the current group is empty; pressing it with
 *     an invalid value marks the group touched (errors appear) and stays.
 *   - Back keeps every value. Values live in component state only (§7 rule 6).
 *   - Submit is on the last step only and posts every visible field of ALL groups once.
 *   - A `WIDGET_SUBMIT_INVALID` verdict lands on its fields and re-enters the first failing step.
 *   - Unmounted while shown and never submitted → `widget_abandoned_v1{kind, group}`.
 */
export default function DataCollectionWidget({ envelope, idPrefix, submitter }) {
	const { msg, lang } = useWidgetMsg();
	const p = envelope.payload || {};
	const groups = Array.isArray(p.groups) ? p.groups : [];
	const stepper = p.layout === "stepper" || (p.layout !== "form" && groups.length > 1);
	const form = useWidgetForm(p, {
		required: msg("widget.required", "This field is required."),
		invalid: msg("widget.invalid", "This value is not valid."),
	});
	const headingRef = useRef(null);
	const submittedRef = useRef(false);
	const groupRef = useRef(form.group);
	groupRef.current = form.group;
	const firstRender = useRef(true);
	const { applyServerErrors } = form;

	useEffect(() => () => {
		if (!submittedRef.current) reportWidgetEvent("widget_abandoned_v1", { kind: "data_collection", group: groupRef.current });
	}, []);

	useEffect(() => {
		if (!submitter.rejection) return;
		submittedRef.current = false;
		applyServerErrors(submitter.rejection.fields);
	}, [submitter.rejection, applyServerErrors]);

	// A step change moves focus to the new step's heading, so keyboard and screen-reader users land there.
	useEffect(() => {
		if (firstRender.current) {
			firstRender.current = false;
			return;
		}
		headingRef.current?.focus();
	}, [form.group]);

	// A `form` layout shows every group at once and submits from its one footer; a stepper walks them.
	const last = !stepper || form.group >= groups.length - 1;
	const sending = submitter.status === "sending";
	const shown = (i) => form.groupFields(i);

	const next = () => {
		form.touch(shown(form.group).map((f) => f.name));
		if (form.groupValid(form.group)) form.setGroup(form.group + 1);
	};
	const submit = () => {
		const all = groups.flatMap((_, i) => shown(i));
		form.touch(all.map((f) => f.name));
		const bad = groups.findIndex((_, i) => !form.groupValid(i));
		if (bad >= 0) {
			form.setGroup(bad);
			return;
		}
		submittedRef.current = true;
		submitter.submit(form.submission());
	};

	const group = groups[form.group] || {};
	const blocked = stepper ? form.groupHasEmptyRequired(form.group) : groups.some((_, i) => form.groupHasEmptyRequired(i));
	const fieldControl = (field) => (
		<FieldControl
			key={field.name}
			id={`${idPrefix}-${field.name}`}
			field={field}
			value={form.values[field.name]}
			other={form.values[otherKey(field.name)]}
			error={form.errorOf(field)}
			onChange={(v) => form.setValue(field.name, v)}
			onOtherChange={(v) => form.setValue(otherKey(field.name), v)}
			onBlur={() => form.touch([field.name])}
			msg={msg}
			lang={lang}
		/>
	);
	const formErrId = form.formError ? `${idPrefix}-form-error` : undefined;
	const footer = (
		<>
			{stepper && form.group > 0 && (
				<WidgetButton data-testid="widget-back" variant="ghost" onClick={() => form.setGroup(form.group - 1)} disabled={sending}>
					{msg("widget.back", "Back")}
				</WidgetButton>
			)}
			{last ? (
				<WidgetButton data-testid="widget-submit" onClick={submit} disabled={blocked || sending || !submitter.canSubmit} aria-describedby={formErrId}>
					{p.submitLabel || msg("widget.submit", "Send")}
				</WidgetButton>
			) : (
				<WidgetButton data-testid="widget-next" onClick={next} disabled={blocked}>{msg("widget.next", "Next")}</WidgetButton>
			)}
		</>
	);

	return (
		<WidgetFrame kind="data_collection" title={p.title} subtitle={p.intro} footer={footer}>
			<Box data-testid="widget-layout" data-layout={stepper ? "stepper" : "form"} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
				{stepper && (
					<Box>
						<Stepper activeStep={form.group} aria-hidden="true" sx={{ mb: 0.5, "& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": { color: "var(--w-accent)" } }}>
							{groups.map((g, i) => <Step key={i} completed={i < form.group}><StepLabel /></Step>)}
						</Stepper>
						<Typography sx={{ fontSize: 11.5 }}>
							{msg("widget.step_of", "Step {current} of {total}", { current: form.group + 1, total: groups.length })}
						</Typography>
					</Box>
				)}
				{stepper ? (
					<>
						{group.title && (
							<Typography ref={headingRef} tabIndex={-1} component="p" data-step-heading sx={{ fontSize: 12.5, fontWeight: 500, outline: "none" }}>
								{group.title}
							</Typography>
						)}
						{group.subtitle && <Typography sx={{ fontSize: 11.5 }}>{group.subtitle}</Typography>}
						{shown(form.group).map(fieldControl)}
					</>
				) : groups.map((g, i) => (
					// every group of a `form` layout, in order, each under its own heading (the first one only when
					// it is not the widget title) — pass-2 P2-H-WM09-1: a stepper threshold above the group count
					<React.Fragment key={i}>
						{(i > 0 || g.title !== p.title) && g.title && (
							<Typography ref={i === form.group ? headingRef : undefined} tabIndex={-1} component="p" data-step-heading sx={{ fontSize: 12.5, fontWeight: 500, outline: "none" }}>
								{g.title}
							</Typography>
						)}
						{g.subtitle && <Typography sx={{ fontSize: 11.5 }}>{g.subtitle}</Typography>}
						{shown(i).map(fieldControl)}
					</React.Fragment>
				))}
				{form.formError && <Typography id={formErrId} sx={{ fontSize: 11.5, color: "var(--w-error)" }}>{form.formError}</Typography>}
			</Box>
		</WidgetFrame>
	);
}
